import Phaser from 'phaser';
import { NetworkManager } from '../managers/NetworkManager';
import { Card } from '../objects/Card';
import { applyRenderScale, RENDER_SCALE } from '../config';
import {
  ClientGameState,
  Card as CardType,
  Suit,
  PlayerPosition,
  GamePhase,
  GAME_WIDTH,
  GAME_HEIGHT,
  HAKEM_DISCARD_COUNT,
  OPPONENT_DISCARD_COUNT,
  AnimationEvent,
  AnimationSequenceId,
} from '@hokm/shared';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { HelpSidebar } from '../objects/HelpSidebar';
import { Scoreboard, ScoreboardSlot } from '../objects/Scoreboard';
import { TrumpSelector } from '../objects/TrumpSelector';
import { UrlStateManager } from '../utils/UrlStateManager';
import { SceneTransition, TRANSITION_DURATIONS } from '../utils/SceneTransition';
import { LayoutManager, LayoutZones } from '../utils/LayoutManager';
import { PlayerPositionConfig, PLAYER_HAND_Y } from '../config/positions';

// Extracted modules
import { AnnouncementQueue } from './game/controllers/AnnouncementQueue';
import { DiscardController } from './game/controllers/DiscardController';
import { TrickAnimator } from './game/animations/TrickAnimator';
import { DealingAnimator } from './game/animations/DealingAnimator';
import { CeremonyAnimator } from './game/animations/CeremonyAnimator';
import { HandRenderer, HandRendererState } from './game/controllers/HandRenderer';
import { PlayerLabels } from './game/ui/PlayerLabels';
import { NetworkEventHandler } from './game/handlers/NetworkEventHandler';
import { TurnToken } from './game/ui/TurnToken';
import { DeckVisual } from './game/ui/DeckVisual';
import { LeaveModal } from './game/ui/LeaveModal';
import { DisconnectNotifications } from './game/ui/DisconnectNotifications';
import {
  toDisplayPosition as utilToDisplayPosition,
  getPositionConfigForState,
  getTrickOffsetsForState,
  getPlayerCount,
} from './game/utils/PositionHelpers';
import { TransitionOverlayScene } from './TransitionOverlayScene';
import {
  getScoreboardPosition,
  updateScoreboard as scoreboardUpdate,
  showHandResult,
  getSlot,
} from './game/utils/ScoreboardHelpers';

export class GameScene extends Phaser.Scene {
  private networkManager!: NetworkManager;
  private gameState: ClientGameState | null = null;

  // Card state
  private pendingCardPlay: CardType | null = null; // For optimistic UI

  // UI elements
  private playerLabelsModule!: PlayerLabels;

  // Circular scoreboard component (all modes: 2P, 3P, 4P)
  private circularScoreboard!: Scoreboard;

  // Mystical trump selector with floating orbs
  private trumpSelector!: TrumpSelector;

  // Announcement queue module
  private announcementQueueModule!: AnnouncementQueue;

  private awaitingTrickCollection: boolean = false; // Blocks interaction from last card click until trick collected

  // Track processed hakem reveal cards to skip duplicate events
  private processedHakemReveals: Set<string> = new Set();

  // 2-player discard controller
  private discardController!: DiscardController;

  // Trick animation module
  private trickAnimator!: TrickAnimator;

  // Dealing animation module
  private dealingAnimator!: DealingAnimator;

  // Ceremony animation module
  private ceremonyAnimator!: CeremonyAnimator;

  // Hand renderer module
  private handRenderer!: HandRenderer;

  // Dealing animation state
  private deckVisual!: DeckVisual;
  private dealtCardIds: Set<string> = new Set(); // Cards that have completed deal animation
  private opponentDealtCounts: Map<PlayerPosition, number> = new Map(); // Track cards dealt to each opponent
  private isDealing: boolean = false;

  // Random card position offsets (keyed by card ID for consistency)
  private cardOffsets: Map<string, { x: number; y: number; rotation: number }> = new Map();

  // Leave room button
  private leaveButton!: Phaser.GameObjects.Container;

  // Disconnect notifications module
  private disconnectNotificationsModule!: DisconnectNotifications;

  // Turn token module
  private turnTokenModule!: TurnToken;

  // Leave modal module
  private leaveModal!: LeaveModal;

  // Help sidebar
  private helpSidebar!: HelpSidebar;

  // Network event handler module
  private networkEventHandler!: NetworkEventHandler;

  constructor() {
    super({ key: 'GameScene' });
  }

  /**
   * Convert server position to display position.
   */
  private toDisplayPosition(serverPosition: PlayerPosition): PlayerPosition {
    const myPosition = this.gameState?.myPosition ?? PlayerPosition.SOUTH;
    return utilToDisplayPosition(serverPosition, myPosition, getPlayerCount(this.gameState));
  }

  /**
   * Get player position configuration based on current player count.
   */
  private getPositionConfig(): Record<PlayerPosition, PlayerPositionConfig> {
    return getPositionConfigForState(this.gameState);
  }

  /**
   * Get trick offsets based on current player count.
   */
  private getTrickOffsetsConfig(): Record<PlayerPosition, { x: number; y: number }> {
    return getTrickOffsetsForState(this.gameState);
  }

  /**
   * Queue an announcement to prevent overlaps.
   */
  private queueAnnouncement(showFn: () => Promise<void>): void {
    this.announcementQueueModule.enqueue(showFn);
  }

  /**
   * Clear announcement queue.
   */
  private clearAnnouncementQueue(): void {
    this.announcementQueueModule.clear();
  }

  create(data?: { fromOverlay?: boolean; reconnecting?: boolean }): void {
    applyRenderScale(this);

    this.networkManager = this.registry.get('networkManager');
    this.gameState = this.registry.get('gameState');

    this.createUI();

    // Initialize announcement queue module
    this.announcementQueueModule = new AnnouncementQueue(this);

    // Initialize turn token module
    this.turnTokenModule = new TurnToken(
      this,
      () => this.getPositionConfig(),
      () => this.gameState?.players.length ?? 4
    );

    this.setupNetworkListeners();

    // Ensure URL has room param for reconnection support (#20)
    const roomId = this.networkManager.getRoomId?.();
    if (roomId) {
      UrlStateManager.updateUrl({ room: roomId });
    }

    // Set up layout change listener for dynamic positioning
    this.layoutChangeHandler = this.handleLayoutChange.bind(this);
    LayoutManager.getInstance().onLayoutChange(this.layoutChangeHandler);

    if (data?.fromOverlay) {
      // Continue card animation from TransitionOverlayScene
      this.handleOverlayTransition();
    } else if (data?.reconnecting) {
      // Reconnection: render immediately without animations
      if (this.gameState) {
        this.renderGameState(this.gameState);
      }
    } else {
      // Fresh game start: fade in and card fly-in effect
      SceneTransition.fadeIn(this, { duration: TRANSITION_DURATIONS.toGame, color: 'feltGreen' });

      // Initial render
      if (this.gameState) {
        this.renderGameState(this.gameState);
      }

      // Card fly-in entrance effect (after a brief delay for fade-in to settle)
      this.time.delayedCall(200, () => {
        SceneTransition.cardFlyIn(this);
      });
    }
  }

  private layoutChangeHandler: ((layout: LayoutZones) => void) | null = null;

  private handleLayoutChange(_layout: LayoutZones): void {
    // Re-apply render scale when layout changes (window resize)
    applyRenderScale(this);

    // Re-render game state to update positions
    if (this.gameState) {
      this.renderGameState(this.gameState);
    }
  }

  /**
   * Handle the continuous card animation from TransitionOverlayScene.
   * Cards continue from their scattered positions to form the player's hand.
   */
  private handleOverlayTransition(): void {
    const overlay = this.scene.get('TransitionOverlayScene') as TransitionOverlayScene | undefined;
    const cards = this.gameState?.myHand ?? [];

    // Fallback if no overlay, no game state, or no cards dealt yet
    if (!overlay?.isActive() || !this.gameState || cards.length === 0) {
      if (this.gameState) {
        this.renderGameState(this.gameState);
      }
      overlay?.cleanup();
      return;
    }

    // Calculate hand positions (same logic as HandRenderer.renderHand)
    const cardSpacing = Math.min(60, (GAME_WIDTH - 200) / cards.length);
    const startX = GAME_WIDTH / 2 - ((cards.length - 1) * cardSpacing) / 2;

    // Arc parameters for curved hand
    const arcHeight = 15;
    const arcRotation = 0.08;

    const positions: Array<{ x: number; y: number; rotation: number }> = cards.map((_, i) => {
      const normalizedPos = cards.length > 1 ? (i / (cards.length - 1)) * 2 - 1 : 0;
      const arcY = arcHeight * (normalizedPos * normalizedPos);
      const arcRot = -arcRotation * normalizedPos;

      return {
        x: startX + i * cardSpacing,
        y: PLAYER_HAND_Y + arcY,
        rotation: arcRot,
      };
    });

    // Get card texture keys
    const cardKeys = cards.map((card) => `card-${card.suit}-${card.rank}`);

    // Continue the animation from overlay
    overlay.continueToHand(positions, cardKeys, () => {
      // Animation complete - clean up overlay and render game state
      overlay.cleanup();

      // Now render the full game state (hand will be re-created by HandRenderer)
      if (this.gameState) {
        this.renderGameState(this.gameState);
      }
    });
  }

  private createUI(): void {
    // Felt texture background (tiled)
    if (this.textures.exists('felt-texture')) {
      const feltBg = this.add.tileSprite(0, 0, GAME_WIDTH, GAME_HEIGHT, 'felt-texture');
      feltBg.setOrigin(0, 0);
      // Scale down high-res tile to world coordinates
      feltBg.setTileScale(1 / RENDER_SCALE);
    }

    // Table ellipse with bronze border
    this.add.ellipse(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, 500, 300, PERSIAN_THEME.colors.tableCenter);
    this.add
      .ellipse(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 30, 504, 304)
      .setStrokeStyle(2, PERSIAN_THEME.colors.bronze, 0.6);

    // Trump indicator removed - now shown on hakem nameplate

    // Unified scoreboard (all modes) - top center
    this.createScoreboard();

    // Player labels module (includes hakem banner variant)
    this.playerLabelsModule = new PlayerLabels(this, {
      getPositionConfig: () => this.getPositionConfig(),
      toDisplayPosition: (pos) => this.toDisplayPosition(pos),
    });

    // Trump selector (hidden initially)
    this.createTrumpSelector();

    // Deck visual (hidden initially, shown during dealing)
    this.createDeckVisual();

    // Help sidebar
    this.helpSidebar = new HelpSidebar(this);

    // Leave modal module
    this.leaveModal = new LeaveModal(this, {
      onLeave: () => this.handleLeaveRoom(),
      getGameState: () => this.gameState,
    });

    // Disconnect notifications module
    this.disconnectNotificationsModule = new DisconnectNotifications(this, {
      onReplaceWithAI: (playerId: string) => this.networkManager.replaceWithAI(playerId),
      toDisplayPosition: (pos: PlayerPosition) => this.toDisplayPosition(pos),
      getPositionConfig: () => this.getPositionConfig(),
      isHost: () => this.isHost(),
    });

    // Discard controller (2-player mode)
    this.discardController = new DiscardController(this, {
      onDiscardConfirmed: (cardIds: string[]) => this.networkManager.discardCards(cardIds),
      onRenderHand: () => {
        if (this.gameState) this.handRenderer.renderHand(this.gameState, this.getHandState());
      },
      getHandCards: () => this.handRenderer.getHandCards(),
      setHandCardsPlayable: (playable: boolean) => this.handRenderer.setHandCardsPlayable(playable),
    });

    // Trick animation module
    this.trickAnimator = new TrickAnimator(this, {
      getPositionConfig: () => this.getPositionConfig(),
      getTrickOffsetsConfig: () => this.getTrickOffsetsConfig(),
      toDisplayPosition: (pos) => this.toDisplayPosition(pos),
      getGameState: () => this.gameState,
      sendAnimationAck: (event, seqId) => this.networkManager.sendAnimationAck(event, seqId),
      onTrickCollected: () => {
        this.awaitingTrickCollection = false;
        this.handRenderer.renderHand(this.gameState!, this.getHandState());
      },
    });

    // Dealing animation module
    this.dealingAnimator = new DealingAnimator(this, {
      getPositionConfig: () => this.getPositionConfig(),
      toDisplayPosition: (pos) => this.toDisplayPosition(pos),
      getMyPlayerId: () => this.gameState?.myPlayerId,
      getCardRandomOffset: (cardId) => this.getCardRandomOffset(cardId),
      sendAnimationAck: (event, seqId) => this.networkManager.sendAnimationAck(event, seqId),
      onCardDealt: (cardId, card, recipientId, displayPosition) => {
        if (this.trickAnimator.isCollectingTrick) return;
        const isMe = recipientId === this.gameState?.myPlayerId;
        if (isMe && this.gameState) {
          this.dealtCardIds.add(cardId);
          if (!this.gameState.myHand.find((c) => c.id === cardId)) {
            this.gameState.myHand.push(card);
          }
          this.handRenderer.renderHand(this.gameState, this.getHandState());
        } else if (this.gameState) {
          const currentCount = this.opponentDealtCounts.get(displayPosition) || 0;
          this.opponentDealtCounts.set(displayPosition, currentCount + 1);
          this.handRenderer.renderOpponentCards(this.gameState, this.getHandState());
        }
      },
      updateDeckVisual: (cardsRemaining) => this.updateDeckVisual(cardsRemaining),
      getDealtCardCount: () => this.dealtCardIds.size,
      getPlayerCount: () => this.gameState?.players.length || 4,
      isCollectingTrick: () => this.trickAnimator.isCollectingTrick,
    });

    // Ceremony animation module
    this.ceremonyAnimator = new CeremonyAnimator(this, {
      getPositionConfig: () => this.getPositionConfig(),
      toDisplayPosition: (pos) => this.toDisplayPosition(pos),
      sendAnimationAck: (event, seqId) => this.networkManager.sendAnimationAck(event, seqId),
      getPlayerLabelContainer: (position) => {
        const data = this.playerLabelsModule.getContainer(position);
        if (!data) return undefined;
        return { bg: data.bg, hakemBanner: data.hakemBanner };
      },
      getCurrentHakemPosition: () => this.playerLabelsModule.getCurrentHakemPosition(),
    });

    // Hand renderer module
    this.handRenderer = new HandRenderer(this, {
      getPositionConfig: () => this.getPositionConfig(),
      toDisplayPosition: (pos) => this.toDisplayPosition(pos),
      canPlayCard: (card, state) => this.canPlayCard(card, state),
      getCardRandomOffset: (cardId) => this.getCardRandomOffset(cardId),
      onCardClicked: (card, cardData) => this.handleCardClicked(card, cardData),
    });

    // Leave room button (top-left)
    this.createLeaveButton();
  }

  private createLeaveButton(): void {
    this.leaveButton = this.add.container(50, 30);
    this.leaveButton.setDepth(20);

    // Button background
    const bg = this.add
      .rectangle(0, 0, 70, 28, PERSIAN_THEME.colors.tableCenter, 0.9)
      .setStrokeStyle(1, PERSIAN_THEME.colors.bronze);

    // Button text
    const text = this.add
      .text(0, 0, 'Leave', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '14px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);

    this.leaveButton.add([bg, text]);

    // Make interactive
    bg.setInteractive({ useHandCursor: true });

    bg.on('pointerover', () => {
      bg.setFillStyle(PERSIAN_THEME.colors.buttonHover);
      text.setColor(PERSIAN_THEME.hex.textPrimary);
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(PERSIAN_THEME.colors.tableCenter, 0.9);
      text.setColor(PERSIAN_THEME.hex.textSecondary);
    });

    bg.on('pointerdown', () => {
      this.leaveModal.show();
    });
  }

  private handleLeaveRoom(): void {
    // Clean up network listeners
    this.shutdown();

    // Leave the room and disconnect
    this.networkManager.leaveRoom();
    this.networkManager.disconnect();

    // Clear URL state and session to prevent auto-rejoin
    UrlStateManager.clearRoomParams();
    UrlStateManager.clearSession();

    // Cards scatter and fly out to transition back to lobby
    SceneTransition.cardFlyOut(this, 'LobbyScene');
  }

  private createScoreboard(): void {
    // Create circular scoreboard at top-right corner (out of the way of cards)
    this.circularScoreboard = new Scoreboard(this, GAME_WIDTH - 85, 75);
    this.circularScoreboard.setVisible(false);
  }

  private createTrumpSelector(): void {
    this.trumpSelector = new TrumpSelector(this);

    // When a suit is selected, send to server
    this.trumpSelector.onSelect((suit: Suit) => {
      this.networkManager.selectTrump(suit);
    });

    // When hovering over a suit, highlight matching cards in hand with trump glow
    this.trumpSelector.onHover((suit: Suit | null) => {
      this.handRenderer.getHandCards().forEach((card) => {
        const shouldHighlight = suit !== null && card.cardData.suit === suit;
        card.setTrump(shouldHighlight);
      });
    });
  }

  private createDeckVisual(): void {
    this.deckVisual = new DeckVisual(this);
  }

  private updateDeckVisual(cardsRemaining: number): void {
    this.deckVisual.update(cardsRemaining);
  }

  /**
   * Update turn token position and animation state
   */
  private updateTurnToken(targetPosition: PlayerPosition | null, needsAction: boolean): void {
    this.turnTokenModule.update(targetPosition, needsAction);
  }

  private getCardRandomOffset(cardId: string): { x: number; y: number; rotation: number } {
    if (!this.cardOffsets.has(cardId)) {
      this.cardOffsets.set(cardId, {
        x: (Math.random() - 0.5) * 8, // ±4px horizontal
        y: (Math.random() - 0.5) * 8, // ±4px vertical
        rotation: (Math.random() - 0.5) * 0.052, // ±1.5° in radians
      });
    }
    return this.cardOffsets.get(cardId)!;
  }

  private getHandState(): HandRendererState {
    return {
      pendingCardPlay: this.pendingCardPlay,
      awaitingTrickCollection: this.awaitingTrickCollection,
      discardMode: this.discardController.isActive,
      isDealing: this.isDealing,
      dealtCardIds: this.dealtCardIds,
      opponentDealtCounts: this.opponentDealtCounts,
    };
  }

  private handleCardClicked(card: Card, cardData: CardType): void {
    // Handle discard mode (2-player)
    if (this.discardController.isActive) {
      this.discardController.toggleSelection(card, cardData);
      return;
    }

    // Use current gameState, not the captured closure state
    const currentState = this.gameState;
    if (!currentState) return;

    if (this.canPlayCard(cardData, currentState) && !this.pendingCardPlay) {
      // Set pending card for optimistic UI
      this.pendingCardPlay = cardData;

      // Get position before destroying the card object
      const startX = card.x;
      const startY = card.y;

      // Remove from hand and destroy
      this.handRenderer.removeCard(card);
      card.destroy();

      // Immediately animate this card to the trick area
      this.trickAnimator.playCardOptimistically(startX, startY, cardData);

      // Disable all remaining cards while waiting for server
      this.handRenderer.setHandCardsPlayable(false);

      // If we just played our last card, block all interaction until trick is collected
      if (this.handRenderer.getHandCards().length === 0) {
        this.awaitingTrickCollection = true;
      }

      // Send to server
      this.networkManager.playCard(cardData.id);
    }
  }

  private setupNetworkListeners(): void {
    this.networkEventHandler = new NetworkEventHandler(this, this.networkManager, {
      // State access
      getGameState: () => this.gameState,
      getMyPosition: () => this.gameState?.myPosition ?? PlayerPosition.SOUTH,
      toDisplayPosition: (pos) => this.toDisplayPosition(pos),

      // State flags (delegate to TrickAnimator)
      isCollectingTrick: () => this.trickAnimator.isCollectingTrick,
      getLastCollectedTrickNumber: () => this.trickAnimator.lastCollectedTrick,
      getPendingCardPlay: () => this.pendingCardPlay,

      // State updates
      setGameState: (state) => {
        this.gameState = state;
      },
      clearPendingCardPlay: () => {
        this.pendingCardPlay = null;
      },
      resetCollectionState: () => {
        this.trickAnimator.reset();
        this.awaitingTrickCollection = false;
        this.pendingCardPlay = null;
      },

      // Deduplication (delegate to TrickAnimator and local set)
      hasProcessedCardPlay: (key) => this.trickAnimator.hasProcessedCardPlay(key),
      markCardPlayProcessed: (key) => this.trickAnimator.markCardPlayProcessed(key),
      hasProcessedHakemReveal: (key) => this.processedHakemReveals.has(key),
      markHakemRevealProcessed: (key) => this.processedHakemReveals.add(key),
      clearProcessedCardPlays: () => this.trickAnimator.reset(),
      clearProcessedHakemReveals: () => this.processedHakemReveals.clear(),

      // Rendering
      renderGameState: (state) => this.renderGameState(state),

      // Trick animations
      animateCardToTrick: (pos, card) => this.trickAnimator.animateCardToTrick(pos, card),
      animateTrickCollection: (pos, seqId) => this.trickAnimator.animateCollection(pos, seqId),
      clearTrickCards: () => this.trickAnimator.reset(),
      destroyHandCards: () => this.handRenderer.destroy(),

      // Scoreboard
      updateScoreboardForTrickWon: (winnerPosition, isTeamMode, isLocalTeamA, handScore, winnerId) => {
        this.updateScoreboardForTrickWon(winnerPosition, isTeamMode, isLocalTeamA, handScore, winnerId);
      },
      setTrumpSuit: (suit, animate) => this.circularScoreboard.setTrumpSuit(suit, animate),

      // Hand completion
      showHandResult: (data) => showHandResult(this.circularScoreboard, this.gameState, data),
      clearTableForNewHand: (seqId) => this.clearTableForNewHand(seqId),

      // Game over
      handleGameOver: (data) => {
        this.registry.set('finalScore', data);
        SceneTransition.cardFlyOut(this, 'GameOverScene');
      },

      // Ceremony animations
      animateHakemCardReveal: (data) => this.ceremonyAnimator.animateHakemCardReveal(data),
      queueAnnouncement: (fn) => this.queueAnnouncement(fn),
      showHakemSelected: (data) => this.ceremonyAnimator.showHakemSelected(data),
      showPartnerSelected: (data) => this.ceremonyAnimator.showPartnerSelected(data),
      showSeatsSwapped: (data) => this.ceremonyAnimator.showSeatsSwapped(data),

      // Dealing animations
      animateCardDealt: (data) => this.dealingAnimator.animateCardDealt(data),
      animateStockDraw: (data) => this.dealingAnimator.animateStockDraw(data),

      // Discard
      showDiscardUI: (data) => this.discardController.enter(data.count),
      clearDiscardMode: () => this.discardController.exit(),

      // Reconnection
      clearReconnectionState: () => {
        this.pendingCardPlay = null;
        this.awaitingTrickCollection = false;
      },
      clearAnnouncementQueue: () => this.clearAnnouncementQueue(),
      clearDealtTracking: () => {
        this.dealtCardIds.clear();
        this.opponentDealtCounts.clear();
      },

      // Disconnect notifications
      showDisconnectNotification: (playerId, name, pos) => this.disconnectNotificationsModule.show(playerId, name, pos),
      hideDisconnectNotification: (playerId) => this.disconnectNotificationsModule.hide(playerId),

      // Kicked
      handleKicked: (reason) => {
        console.log('Kicked from game:', reason);
        this.networkManager.disconnect();
        UrlStateManager.clearRoomParams();
        UrlStateManager.clearSession();
        SceneTransition.cardFlyOut(this, 'LobbyScene', { fromGame: true });
      },

      // Animation ack
      sendAnimationAck: (event, seqId) => this.networkManager.sendAnimationAck(event, seqId),
    });
  }

  /**
   * Update scoreboard UI when a trick is won.
   */
  private updateScoreboardForTrickWon(
    winnerPosition: PlayerPosition,
    isTeamMode: boolean,
    isLocalTeamA: boolean,
    handScore: { teamA: number; teamB: number } | Record<string, number>,
    winnerId?: string
  ): void {
    if (isTeamMode) {
      const teamHandScore = handScore as { teamA: number; teamB: number };
      const winnerIsTeamA = winnerPosition % 2 === 0;
      const localHandScore = isLocalTeamA ? teamHandScore.teamA : teamHandScore.teamB;
      const opponentHandScore = isLocalTeamA ? teamHandScore.teamB : teamHandScore.teamA;
      const isLocalTeamWin = winnerIsTeamA === isLocalTeamA;

      // Set previous tricks (not the new one being animated)
      this.circularScoreboard.setSlotTricks(0, isLocalTeamWin ? localHandScore - 1 : localHandScore);
      this.circularScoreboard.setSlotTricks(2, isLocalTeamWin ? opponentHandScore : opponentHandScore - 1);

      // Animate the winning team's new dot
      const winnerSlot: ScoreboardSlot = isLocalTeamWin ? 0 : 2;
      const trickIndex = (isLocalTeamWin ? localHandScore : opponentHandScore) - 1;
      this.circularScoreboard.animateSlotTrickWon(winnerSlot, trickIndex);
    } else if (winnerId && this.gameState) {
      const individualScore = handScore as Record<string, number>;
      const trickIndex = individualScore[winnerId] - 1;
      const winnerSlot = getSlot(this.gameState, winnerId);
      this.circularScoreboard.setSlotTricks(winnerSlot, trickIndex);
      this.circularScoreboard.animateSlotTrickWon(winnerSlot, trickIndex);
    }
  }

  private renderGameState(state: ClientGameState): void {
    // Update player labels (handles positioning, colors, hakem banner)
    this.playerLabelsModule.reposition();
    this.playerLabelsModule.update(state);

    // Update unified scoreboard (handles both team and individual modes)
    scoreboardUpdate(this.circularScoreboard, state);

    // Update turn token position and animation
    const isReadyToDeal = state.phase === GamePhase.READY_TO_DEAL;
    const isHakem = state.hakemId === state.myPlayerId;

    // Determine token position and whether local player needs to act
    let tokenPosition: PlayerPosition | null = null;
    let needsAction = false;

    if (state.phase === GamePhase.SELECTING_HAKEM) {
      // Hide token during hakem selection (no active turn yet)
      tokenPosition = null;
    } else if (isReadyToDeal) {
      // Waiting to deal - token at Hakem, pulse if local player is Hakem
      const hakem = state.players.find((p) => p.id === state.hakemId);
      if (hakem) {
        tokenPosition = this.toDisplayPosition(hakem.position);
        needsAction = isHakem;
      }
    } else if (state.isSelectingTrump) {
      // Trump selection - token at Hakem, pulse only if local player needs to select
      const hakem = state.players.find((p) => p.id === state.hakemId);
      if (hakem) {
        tokenPosition = this.toDisplayPosition(hakem.position);
        needsAction = true; // isSelectingTrump is only true for the Hakem
      }
    } else if (state.phase === GamePhase.HAKEM_DISCARDING) {
      // Hakem discarding - token at Hakem, pulse if local player is Hakem
      const hakem = state.players.find((p) => p.id === state.hakemId);
      if (hakem) {
        tokenPosition = this.toDisplayPosition(hakem.position);
        needsAction = isHakem;
      }
    } else if (state.phase === GamePhase.OPPONENT_DISCARDING) {
      // Opponent discarding - token at opponent, no pulse
      const opponent = state.players.find((p) => p.id !== state.myPlayerId);
      if (opponent) {
        tokenPosition = this.toDisplayPosition(opponent.position);
        needsAction = false;
      }
    } else if (
      state.phase === GamePhase.DEALING ||
      state.phase === GamePhase.TRUMP_SELECTION ||
      state.phase === GamePhase.DRAWING_FROM_STOCK
    ) {
      // Automatic phases - token at Hakem but no action needed
      const hakem = state.players.find((p) => p.id === state.hakemId);
      if (hakem) {
        tokenPosition = this.toDisplayPosition(hakem.position);
        needsAction = false;
      }
    } else if (state.phase === GamePhase.PLAYING && state.currentPlayerId) {
      // Playing phase - token follows current player, pulse if it's my turn
      const currentPlayer = state.players.find((p) => p.id === state.currentPlayerId);
      if (currentPlayer) {
        tokenPosition = this.toDisplayPosition(currentPlayer.position);
        needsAction = state.isMyTurn;
      }
    }

    this.updateTurnToken(tokenPosition, needsAction);

    // Show/hide trump selector
    if (state.isSelectingTrump) {
      // Set scoreboard position for fly-to animation
      const pos = getScoreboardPosition(state.playerCount);
      this.trumpSelector.setScoreboardPosition(pos.x, pos.y);
      this.trumpSelector.show();
    } else {
      this.trumpSelector.hide();
    }

    // Handle dealing phase - show deck and track state
    // Include all phases where cards are being dealt or drawn from deck
    const wasDealing = this.isDealing;
    const dealingPhases = [
      GamePhase.READY_TO_DEAL,
      GamePhase.TRUMP_SELECTION,
      GamePhase.DEALING,
      GamePhase.HAKEM_DISCARDING,
      GamePhase.OPPONENT_DISCARDING,
      GamePhase.DRAWING_FROM_STOCK, // 2P: drawing cards from stock after discard
    ];
    this.isDealing = dealingPhases.includes(state.phase);

    // Show deck during phases where cards are being dealt or could be dealt
    const deckVisiblePhases = [
      GamePhase.SELECTING_HAKEM,
      GamePhase.READY_TO_DEAL,
      GamePhase.DEALING,
      GamePhase.TRUMP_SELECTION,
      GamePhase.HAKEM_DISCARDING, // 2P: opponent cards dealt during hakem discard
      GamePhase.OPPONENT_DISCARDING,
      GamePhase.DRAWING_FROM_STOCK,
    ];
    const showDeck = deckVisiblePhases.includes(state.phase);
    this.deckVisual.setVisible(showDeck);

    // Reset dealt cards tracking when dealing ends
    if (wasDealing && !this.isDealing) {
      this.dealtCardIds.clear();
      this.opponentDealtCounts.clear();
      this.cardOffsets.clear();
    }

    // Don't re-render hand during trick collection animation
    // The animation callback will handle re-rendering when complete
    if (!this.trickAnimator.isCollectingTrick) {
      const handState = this.getHandState();
      this.handRenderer.renderHand(state, handState);
      this.handRenderer.renderOpponentCards(state, handState);
      // Note: pendingCardPlay is cleared in cardPlayed handler or trickWon cleanup
      // Don't clear here - gameState may arrive before cardPlayed event
    }

    // Render current trick
    this.trickAnimator.renderTrick(state.currentTrick.cards, state.trickNumber);

    // Restore discard UI if needed (e.g., after reconnection)
    const needsHakemDiscard = state.phase === GamePhase.HAKEM_DISCARDING && state.hakemId === state.myPlayerId;
    const needsOpponentDiscard = state.phase === GamePhase.OPPONENT_DISCARDING && state.hakemId !== state.myPlayerId;

    if ((needsHakemDiscard || needsOpponentDiscard) && !this.discardController.isActive) {
      const count = needsHakemDiscard ? HAKEM_DISCARD_COUNT : OPPONENT_DISCARD_COUNT;
      this.discardController.enter(count);
    }
  }

  private canPlayCard(card: CardType, state: ClientGameState): boolean {
    if (!state.isMyTurn || state.phase !== GamePhase.PLAYING) {
      return false;
    }

    const leadSuit = state.currentTrick.leadSuit;
    if (!leadSuit) {
      return true; // Leading, any card valid
    }

    // Must follow suit if possible
    const hasSuit = state.myHand.some((c) => c.suit === leadSuit);
    if (hasSuit) {
      return card.suit === leadSuit;
    }

    return true; // Void in lead suit
  }

  /**
   * Clear all cards from the table for a new hand.
   * Animates old cards away, pauses, then processes any queued dealt cards.
   */
  private async clearTableForNewHand(sequenceId?: AnimationSequenceId): Promise<void> {
    this.dealingAnimator.setClearing(true);

    // Reset scoreboard trick dots (they may have been modified by hand-end animation)
    this.circularScoreboard.resetSlotTricks();

    // Clear all cards with animations
    await Promise.all([this.trickAnimator.clearAll(), this.handRenderer.clearAll()]);

    // Clear dealt card tracking
    this.dealtCardIds.clear();
    this.opponentDealtCounts.clear();

    // Brief pause to show cleared table
    await new Promise<void>((resolve) => this.time.delayedCall(300, resolve));

    // Send animation acknowledgment after table is cleared
    if (sequenceId) {
      this.networkManager.sendAnimationAck(AnimationEvent.HAND_CLEARED, sequenceId);
    }

    this.dealingAnimator.setClearing(false);

    // Process any queued dealt cards
    await this.dealingAnimator.processPendingCards();
  }

  shutdown(): void {
    // Remove network event listeners
    this.networkEventHandler.destroy();

    // Clean up layout change listener
    if (this.layoutChangeHandler) {
      LayoutManager.getInstance().offLayoutChange(this.layoutChangeHandler);
      this.layoutChangeHandler = null;
    }

    // Clean up all modules to prevent memory leaks
    this.playerLabelsModule.destroy();
    this.circularScoreboard.destroy();
    this.trumpSelector.destroy();
    this.announcementQueueModule.clear();
    this.discardController.destroy();
    this.trickAnimator.destroy();
    this.dealingAnimator.destroy();
    this.ceremonyAnimator.destroy();
    this.handRenderer.destroy();
    this.deckVisual.destroy();
    this.disconnectNotificationsModule.destroy();
    this.turnTokenModule.destroy();
    this.leaveModal.destroy();
    this.helpSidebar.destroy();
  }

  /**
   * Check if the current player is the host (first player in room).
   */
  private isHost(): boolean {
    if (!this.gameState) return false;
    const firstPlayer = this.gameState.players.find((p) => p !== null);
    return firstPlayer?.id === this.gameState.myPlayerId;
  }

  update(time: number): void {
    // Update scoreboard animations (trump glow pulse)
    this.circularScoreboard?.update(time);

    // Update trump selector floating animations
    this.trumpSelector?.update(time);
  }
}
